import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from "react";
import { useSelector } from "../../../../../../app/helpers";
import { RootState } from "../../../../../../app/rootReducer";
import { IPlaceSchedule } from "../../../../Domain/Types/FloorPlan/PlaceSchedule";
import { IZoneSchedule } from "../../../../Domain/Types/FloorPlan/ZoneSchedule";
import { BookingInputs } from "../../../../Domain/Types/BookingInputs.type";
import { IFloorPayload } from "../../../../Domain/Types/FloorPlan/FloorPayload.type";
import { BookingType } from "../../../../../../features/Booking-Form/typings/booking-inputs";
import { useRemoteFetchMaxSetupTime } from "../../../../../../hooks/Remote/Services/BookingServices/useRemoteFetchMaxSetupTime";
import { useIsUserServiceAdminForLocation } from "../../../../../../hooks/Remote/Admin/useIsUserServiceAdminForLocation";
import { resolveLocationInventoryIdForServiceAdmin } from "./SelectionPrompt.functions";
import { computeSelectionPrompt2Derived } from "./selectionPrompt2Derived";
import { DateTime } from "luxon";

type Props = {
  selected: { place: IPlaceSchedule | undefined; zone: IZoneSchedule | undefined };
  bookingInputs: BookingInputs;
  floorPlanForService: IFloorPayload | undefined;
  selectedZone: number | undefined;
  userId: string;
  selectableByVariant: boolean;
};

export function useSelectionPrompt2ConferenceState({
  selected,
  bookingInputs,
  floorPlanForService,
  selectedZone,
  userId,
  selectableByVariant
}: Props) {
  const selectedEntry = selected.place || selected.zone;

  const { isRequestAdmin } = useSelector((state: RootState) => state.login.userRoles);
  const { activateBookingServices } = useSelector(
    (state: RootState) => state.login.userInformation.company.meta
  );
  const { conferenceService, bookingFrom, bookingStart } = useSelector(
    (state: RootState) => state.booking.inputs
  );

  const { data: isServiceAdminForLocation } = useIsUserServiceAdminForLocation(
    resolveLocationInventoryIdForServiceAdmin(bookingInputs, selectedEntry),
    userId,
    isRequestAdmin
  );
  const isServiceAdminBypass = isServiceAdminForLocation === true;

  const nowDate = DateTime.now().setZone(bookingInputs.timezone).toISO() as string;

  const { data: remoteMaxSetupTime } = useRemoteFetchMaxSetupTime(
    {
      bookingInventoryIds: [selectedEntry?.inventory?.id || selectedZone || 0],
      startDate: bookingInputs.bookingFrom || nowDate,
      endDate: bookingInputs.bookingTo || nowDate,
      startTime: bookingInputs.bookingStart ?? "08:00",
      endTime: bookingInputs.bookingEnd ?? "17:00",
      bookingType: BookingType.CONFERENCEZONE,
      timezone: bookingInputs.timezone,
      weekdays: bookingInputs.weekdays,
      monday: "no use"
    },
    true
  );

  const [isServiceModalOpen, setIsServiceModalOpenState] = useState(false);
  const [isBookingServiceLoading, setIsBookingServiceLoading] = useState(true);
  /** Closing the required-service dialog must not open it again. */
  const serviceModalDismissedRef = useRef(false);

  const setIsServiceModalOpen = useCallback((next: SetStateAction<boolean>) => {
    setIsServiceModalOpenState(previous => {
      const open = typeof next === "function" ? next(previous) : next;
      if (!open) {
        serviceModalDismissedRef.current = true;
      }
      return open;
    });
  }, []);

  const derived = useMemo(() => {
    if (selectedEntry == null) return null;

    return computeSelectionPrompt2Derived({
      bookingInputs,
      floorPlanForService,
      selectedZone,
      bookingFrom,
      bookingStart,
      maxSetupTime: remoteMaxSetupTime?.maxSetUpTime ?? 0,
      conferenceService,
      selectedEntry,
      selectableByVariant,
      isServiceAdminBypass
    });
  }, [
    selectedEntry,
    bookingInputs,
    floorPlanForService,
    selectedZone,
    bookingFrom,
    bookingStart,
    remoteMaxSetupTime?.maxSetUpTime,
    conferenceService,
    selectableByVariant,
    isServiceAdminBypass
  ]);

  useEffect(() => {
    if (derived == null || serviceModalDismissedRef.current) {
      return;
    }

    const { mandatoryServicesFulfilled, hasOrderableService, hasMandatory } = derived;

    if (isServiceModalOpen || mandatoryServicesFulfilled) {
      return;
    }

    if (hasOrderableService && hasMandatory && !mandatoryServicesFulfilled) {
      setIsServiceModalOpenState(true);
    }
  }, [derived, isServiceModalOpen, isBookingServiceLoading]);

  return {
    activateBookingServices,
    selectedEntry,
    isServiceAdminBypass,
    conferenceService,
    bookingFrom,
    bookingStart,
    remoteMaxSetupTime,
    isServiceModalOpen,
    setIsServiceModalOpen,
    isBookingServiceLoading,
    setIsBookingServiceLoading,
    derived
  };
}
